import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, SlidersHorizontal, X, ChevronLeft, ChevronRight,
  UserRound, Users,
} from 'lucide-react';
import { usersAPI } from '../../api/users';
import PageHeader from '../../components/ui/PageHeader';

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const genderColor = {
  male:   { bg: 'rgba(59,130,246,0.15)',  text: '#93c5fd' },
  female: { bg: 'rgba(236,72,153,0.15)',  text: '#f9a8d4' },
  other:  { bg: 'rgba(139,92,246,0.15)',  text: '#c4b5fd' },
};

const GENDERS = ['all', 'male', 'female', 'other'];

const PER_PAGE = 15;

// ── Skeleton row ─────────────────────────────────────────────
function UserSkeleton() {
  return (
    <div className="card flex items-center gap-3">
      <div className="skeleton h-11 w-11 flex-shrink-0 rounded-full" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="skeleton h-3.5 w-32 rounded" />
        <div className="skeleton h-3 w-24 rounded" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="skeleton h-3.5 w-20 rounded" />
        <div className="skeleton h-3 w-16 rounded" />
      </div>
    </div>
  );
}

// ── User card ────────────────────────────────────────────────
function UserCard({ user, onClick }) {
  const initials = user.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const gc = genderColor[user.gender] || genderColor.other;

  return (
    <button
      onClick={onClick}
      className="card w-full text-left flex items-center gap-3 transition-all duration-200
                 hover:border-gold/40 active:scale-[0.98]"
    >
      {/* Avatar */}
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
           style={{ background: 'linear-gradient(135deg, #1448c4 0%, #0a2260 100%)' }}>
        {initials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{user.name}</p>
        <div className="mt-0.5 flex items-center gap-2">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {user.mobile}
          </p>
          <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ background: gc.bg, color: gc.text }}>
            {user.gender}
          </span>
        </div>
      </div>

      {/* Balance */}
      <div className="flex-shrink-0 text-right">
        <p className="text-sm font-semibold text-gold-shimmer">
          {formatINR(user.total_balance)}
        </p>
        <p className="mt-0.5 text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {formatDate(user.registered_at)}
        </p>
      </div>
    </button>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function UsersPage() {
  const navigate = useNavigate();

  // filters
  const [search, setSearch]       = useState('');
  const [gender, setGender]       = useState('all');
  const [from,   setFrom]         = useState('');
  const [to,     setTo]           = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // data
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // debounce search
  const searchDebounce = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(searchDebounce.current);
  }, [search]);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [gender, from, to]);

  // ── Fetch ────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: PER_PAGE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (gender !== 'all') params.gender = gender;
      if (from)  params.from = from;
      if (to)    params.to   = to;

      const { data } = await usersAPI.getList(params);
      if (data.status) {
        setUsers(data.data.users);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, gender, from, to, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Clear filters ────────────────────────────────────────
  const clearFilters = () => {
    setGender('all');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const hasActiveFilters = gender !== 'all' || from || to;

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title="Users"
        right={
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="relative flex h-8 w-8 items-center justify-center rounded-xl transition-all active:scale-90"
            style={{
              background: showFilters
                ? 'rgba(212,160,23,0.2)'
                : 'rgba(255,255,255,0.08)',
              border: showFilters
                ? '1px solid rgba(212,160,23,0.4)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <SlidersHorizontal size={15} style={{ color: showFilters ? '#d4a017' : 'rgba(255,255,255,0.6)' }} />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
                    style={{ background: '#d4a017' }} />
            )}
          </button>
        }
      />

      <div className="flex flex-col gap-3 p-4">

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(255,255,255,0.35)' }} />
          <input
            type="text"
            placeholder="Search by name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 pr-9"
          />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="card flex flex-col gap-4 animate-fade-in">
            {/* Gender tabs */}
            <div>
              <p className="input-label mb-2">Gender</p>
              <div className="flex gap-2 flex-wrap">
                {GENDERS.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGender(g)}
                    className="rounded-xl px-3 py-1.5 text-xs font-semibold capitalize transition-all duration-150"
                    style={{
                      background: gender === g
                        ? 'linear-gradient(135deg,#d4a017,#f0c040)'
                        : 'rgba(255,255,255,0.07)',
                      color: gender === g ? '#fff' : 'rgba(255,255,255,0.55)',
                      border: gender === g
                        ? 'none'
                        : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    {g === 'all' ? 'All' : g}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="input-label">From</label>
                <input type="date" value={from}
                       onChange={(e) => setFrom(e.target.value)}
                       className="input-field" />
              </div>
              <div className="flex flex-col">
                <label className="input-label">To</label>
                <input type="date" value={to}
                       onChange={(e) => setTo(e.target.value)}
                       className="input-field" />
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters}
                      className="flex items-center gap-1.5 self-start text-xs font-medium transition-opacity hover:opacity-70"
                      style={{ color: '#fca5a5' }}>
                <X size={12} /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Summary row ── */}
        {pagination && !loading && (
          <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Showing {users.length} of {pagination.total} user{pagination.total !== 1 ? 's' : ''}
          </p>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="card text-sm text-center py-6" style={{ color: '#fca5a5' }}>
            {error}
          </div>
        )}

        {/* ── Loading skeletons ── */}
        {loading && !error && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 6 }).map((_, i) => <UserSkeleton key={i} />)}
          </div>
        )}

        {/* ── User list ── */}
        {!loading && !error && users.length > 0 && (
          <div className="flex flex-col gap-3">
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onClick={() => navigate(`/users/${user.id}`)}
              />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && users.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <Users size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {debouncedSearch || hasActiveFilters ? 'No users match your filters' : 'No users found'}
            </p>
            {(debouncedSearch || hasActiveFilters) && (
              <button onClick={() => { setSearch(''); clearFilters(); }}
                      className="text-xs font-medium"
                      style={{ color: '#d4a017' }}>
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={15} /> Prev
            </button>

            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Page {pagination.current_page} / {pagination.last_page}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
